// src/features/landing/PageAnalyticsPanel.tsx
// Editor "Analytics" tab — views, unique visitors, bounce rate, visit trend,
// per-section engagement, form/booking conversions. GetLandingAnalytics.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { landingApi } from '@core/api';
import type { LandingAnalytics } from '@core/types/landing';
import { VisitTrendChart } from './charts';
import { errMessage } from './helpers';

export function PageAnalyticsPanel({ pageId }: { pageId: string }) {
  const { t } = useI18n();
  const [data, setData] = useState<LandingAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    landingApi
      .GetLandingAnalytics(pageId)
      .then((res) => setData(res.analytics ?? null))
      .catch((e: unknown) => setError(errMessage(e, t('landing.analytics.loadError'))))
      .finally(() => setLoading(false));
  }, [pageId, t]);

  if (loading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <div className="h-20 animate-pulse rounded-md bg-muted" />
        <div className="h-40 animate-pulse rounded-md bg-muted" />
      </div>
    );
  }
  if (error) return <Alert variant="error">{error}</Alert>;
  if (!data) return <p className="text-sm text-muted-foreground">{t('landing.analytics.empty')}</p>;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label={t('landing.analytics.views')} value={data.total_views} />
        <Stat label={t('landing.analytics.visitors')} value={data.unique_visitors} />
        <Stat label={t('landing.analytics.bounceRate')} value={`${(data.bounce_rate * 100).toFixed(1)}%`} />
        <Stat label={t('landing.analytics.avgTime')} value={`${data.avg_time_on_page_sec}s`} />
      </div>

      <div>
        <h3 className="mb-2 text-sm font-medium text-foreground">{t('landing.analytics.trend')}</h3>
        <VisitTrendChart points={data.visit_trend ?? []} title={t('landing.analytics.trend')} empty={t('landing.analytics.noTrend')} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Stat label={t('landing.analytics.formSubmissions')} value={data.form_submissions} />
        <Stat label={t('landing.analytics.bookingConversions')} value={data.booking_conversions} />
      </div>

      {(data.section_engagement ?? []).length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-medium text-foreground">{t('landing.analytics.sectionEngagement')}</h3>
          <div className="overflow-x-auto">
            <table className="table table-compact w-full">
              <thead>
                <tr>
                  <th className="text-left text-xs font-medium text-muted-foreground">{t('landing.analytics.section')}</th>
                  <th className="text-right text-xs font-medium text-muted-foreground">{t('landing.analytics.impressions')}</th>
                  <th className="text-right text-xs font-medium text-muted-foreground">{t('landing.analytics.clicks')}</th>
                </tr>
              </thead>
              <tbody>
                {data.section_engagement.map((se) => (
                  <tr key={se.section_id}>
                    <td className="text-sm text-foreground">{se.section_type}</td>
                    <td className="text-right text-sm tabular-nums text-foreground">{se.impressions}</td>
                    <td className="text-right text-sm tabular-nums text-foreground">{se.clicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-md border border-border p-3">
      <p className="text-2xl font-semibold tabular-nums text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
